import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  analisarDocumento,
  avaliarEnvio,
  aprovarComprovante,
  conferirCertificado,
  confirmarPagamento,
  emitirDocumento,
  entregarSolicitacao,
  estadoDoDocumento,
  lerAnalises,
  lerAnexos,
  lerComprovantesDoPedido,
  lerFila,
  lerFiltrosDaFila,
  reabrirParaAnalise,
  recusarComprovante,
  urlDoAnexo,
  INSTITUICOES,
  PRAZOS,
  type AnaliseDoArquivo,
  type AnexoDoEnvio,
  type ComprovanteNoBalcao,
  type FiltrosDaFila,
  type FiltrosEscolhidos,
  type Instituicao,
  type ItemDaFila,
  type Prazo,
} from "../lib/faculdade";
import { CAMPO, CampoDeBusca, Recado, SetaDireita } from "./ui";
import {
  BarraDeFiltros,
  CampoDeData,
  Filtro,
  LimparFiltros,
  LinhaDeFiltros,
  dataDaQuery,
} from "./BarraDeFiltros";
import FilaDeAvisos from "./FilaDeAvisos";

// Suporte › Faculdade — a fila do balcão da secretaria.
//
// ---------------------------------------------------------------------------
// A fila tem 12.625 itens, e é por isso que ela tem BUSCA
// ---------------------------------------------------------------------------
// Medido em 31/08: 11.079 aguardando documento, 1.129 aguardando pagamento, 417
// em análise. A tela mostra 200 por vez, do mais antigo para o mais novo — que é
// o certo num balcão —, e sem busca o operador atendendo UM aluno não tem como
// achar o documento dele.
//
// O TOTAL vem em cada linha da RPC e aparece no cabeçalho: "200 de 200" e
// "200 de 12.625" pedem trabalho diferente, e sem o número a tela mente por
// omissão.
//
// ---------------------------------------------------------------------------
// Reprovar EXIGE motivo, e a tela cobra antes do banco
// ---------------------------------------------------------------------------
// A constraint `sec_reprovado_explica` recusa reprovação sem mensagem. Mas
// "violação de check" não diz ao operador o que fazer — então o botão de reprovar
// só liga quando há texto, e o campo explica por quê.
//
// São 17.161 reprovações na base, e 67.664 documentos com mensagem: a equipe já
// trabalha assim. O que muda é que agora não dá para esquecer.

/**
 * Quantos itens a tela mostra de uma vez.
 *
 * MEDIDO: com os 200 que a RPC devolve por padrão, a aba saiu com **41.910
 * pixels** — 46 telas —, e a captura mostrou "Anexar e entregar" repetido 156
 * vezes. Uma fila de atendimento se trabalha do topo; 46 telas de rolagem é a
 * mesma classe de defeito do IR Pro (8.249px para mostrar 34 títulos) e da busca
 * de ocupações (64.608px).
 *
 * Vinte e cinco é o que cabe em ~5 telas, e o TOTAL continua dito no cabeçalho —
 * o operador sabe o tamanho da fila sem precisar rolá-la.
 */
const POR_PAGINA = 25;

/**
 * O rótulo curto de cada status, para os chips do filtro.
 *
 * Os dois últimos não esperam ninguém: eles vêm de `sec_fila_filtros` e o combo
 * marca cada um com "· consulta", porque escolhê-los troca a fila de trabalho
 * por uma consulta de dezenas de milhares de itens. Estão aqui com nome próprio
 * desde que o botão de reprocessar existe (000211): "reprovado" é justamente
 * onde a secretaria vai atrás do que a IA julgou errado, e o valor cru do banco
 * como rótulo de tela é o resto de uma lista que ninguém completou.
 */
const ROTULO_STATUS: Record<string, string> = {
  em_analise: "Em análise",
  aguardando_documento: "Aguardando a secretaria",
  aguardando_pagamento: "Aguardando pagamento",
  comprovante_em_analise: "Comprovante em análise",
  aguardando_conferencia: "Em conferência",
  reprovado: "Reprovados",
  aprovado: "Aprovados",
};

/** De onde sai o diploma (000137) — e o terceiro estado, que hoje é o único. */
const ROTULO_INSTITUICAO: Record<Instituicao, string> = {
  fbc: "FBC",
  focus: "Focus",
  sem: "Não informada",
};

/**
 * As gavetas de prazo (000201). "Vencendo" é um recorte de "No prazo" — faltam
 * até 5 dias —, e o rótulo diz isso para as duas não parecerem exclusivas.
 */
const ROTULO_PRAZO: Record<Prazo, string> = {
  no_prazo: "No prazo",
  vencendo: "Vencendo (até 5 dias, dentro do prazo)",
  atrasado: "Atrasados",
  sem_prazo: "Sem prazo definido",
};

export default function SuporteFaculdade() {
  const [fase, setFase] = useState<"carregando" | "pronto" | "erro">("carregando");
  const [fila, setFila] = useState<ItemDaFila[]>([]);
  const [filtros, setFiltros] = useState<FiltrosDaFila | null>(null);
  const [busca, setBusca] = useState("");
  const [buscando, setBuscando] = useState(false);
  const [mostrando, setMostrando] = useState(POR_PAGINA);
  const [recado, setRecado] = useState<{ tipo: "ok" | "erro"; texto: string } | null>(null);

  // Os filtros viajam na QUERY, não em memória (000159).
  //
  // A doutrina da casa é sobre ABA como sub-rota, e a razão vale igual aqui:
  // "estado que deveria estar no endereço, guardado em memória, é uma tela que
  // não se pode indicar a ninguém". Numa fila de balcão isso acontece todo dia —
  // "olha os 433 em análise da Pós RT" é uma frase que a equipe troca, e sem a
  // query ela vira um roteiro de cliques.
  //
  // Na query e não no caminho, como o Mapa de Concursos: são FILTROS de uma
  // tela, não telas diferentes. E de graça vem o botão de voltar do navegador
  // desfazendo o último filtro, que é o que o operador espera.
  const [params, setParams] = useSearchParams();
  const status = params.get("status");
  const curso = params.get("curso");
  const turma = params.get("turma");
  const tipo = params.get("tipo");
  // O fluxo também: ele era `useState`, e é o filtro que a equipe mais troca de
  // link ("me manda a fila de pedidos"). Valor desconhecido cai em `null`, que é
  // "tudo" — query digitada à mão não pode produzir tela quebrada.
  const fluxoBruto = params.get("fluxo");
  const fluxo: "envio" | "solicitacao" | null =
    fluxoBruto === "envio" || fluxoBruto === "solicitacao" ? fluxoBruto : null;
  // Os quatro da 000201, com a mesma regra do fluxo: valor que a tela não
  // conhece cai em `null`. O banco devolveria VAZIO para `?prazo=xyz` (é a
  // decisão do teste 69), e vazio por causa de um parâmetro que a tela nem
  // mostra é fila que parece quebrada.
  const instituicaoBruta = params.get("instituicao");
  const instituicao: Instituicao | null = (INSTITUICOES as readonly string[]).includes(
    instituicaoBruta ?? "",
  )
    ? (instituicaoBruta as Instituicao)
    : null;
  const prazoBruto = params.get("prazo");
  const prazo: Prazo | null = (PRAZOS as readonly string[]).includes(prazoBruto ?? "")
    ? (prazoBruto as Prazo)
    : null;
  const de = dataDaQuery(params.get("de"));
  const ate = dataDaQuery(params.get("ate"));
  const algumFiltro = Boolean(status || curso || turma || tipo || instituicao || de || ate || prazo);
  // Há instituição CONHECIDA na fila? Na tarde de 09/09 não havia nenhuma (a
  // coluna existia e a carga não tinha vindo), e é isso que liga a nota do
  // painel. A carga entrou à noite (000203) e a nota apagou sozinha.
  const temInstituicao = Boolean(
    filtros?.instituicoes.some((i) => i.valor !== "sem" && i.itens > 0),
  );

  async function carregar(
    f = fluxo,
    b = busca,
    sel: FiltrosEscolhidos = { status, curso, turma, tipo, instituicao, de, ate, prazo },
  ) {
    setBuscando(true);
    try {
      const [itens, ops] = await Promise.all([
        lerFila(f, b.trim() || null, 200, sel),
        lerFiltrosDaFila(f),
      ]);
      setFila(itens);
      setFiltros(ops);
      // Toda carga nova volta ao começo: continuar no item 75 depois de trocar o
      // filtro mostraria uma parte do meio de outra lista.
      setMostrando(POR_PAGINA);
      setFase("pronto");
    } catch {
      setFase("erro");
    } finally {
      setBuscando(false);
    }
  }

  /**
   * Troca um filtro e recarrega — com o valor NOVO, não o do estado.
   *
   * `setState` é assíncrono, então `carregar()` logo depois leria o valor
   * anterior e a tela mostraria o resultado do filtro passado. É a mesma classe
   * da cicatriz do `aoTerminar` que capturava o `cursoAtivo` do clique.
   */
  function trocar(quais: FiltrosEscolhidos, b = busca) {
    const novo: FiltrosEscolhidos = {
      status, curso, turma, tipo, instituicao, de, ate, prazo, ...quais,
    };
    // Trocar de curso zera a turma: a turma 9 da Pós RT não existe na Perícia, e
    // manter o número produziria uma fila vazia que parece defeito.
    if (quais.curso !== undefined && quais.curso !== curso) novo.turma = null;

    // Chave ausente e não vazia: `?status=&curso=` é ruído no endereço que o
    // operador vai copiar.
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(novo)) {
      if (v) p.set(k, v);
      else p.delete(k);
    }
    setParams(p, { replace: true });
    carregar(fluxo, b, novo);
  }

  /**
   * Troca o fluxo e ZERA os outros filtros.
   *
   * As opções de status, curso e turma são contadas POR fluxo — a fila de envios
   * tem `em_analise`, a de pedidos tem `aguardando_documento`. Manter uma
   * seleção que não existe no fluxo novo produz fila vazia parecendo defeito, e
   * o operador não tem como saber que o filtro invisível é a causa.
   */
  function trocarFluxo(novo: "envio" | "solicitacao" | null) {
    const p = new URLSearchParams();
    if (novo) p.set("fluxo", novo);
    setParams(p, { replace: true });
    // `carregar` explícito com os valores novos: o `useEffect` reage a `fluxo`,
    // mas ele leria `status`/`curso`/`turma` do render anterior.
    carregar(novo, busca, {
      status: null, curso: null, turma: null, tipo: null,
      instituicao: null, de: null, ate: null, prazo: null,
    });
  }

  useEffect(() => {
    carregar(fluxo, busca, { status, curso, turma, tipo, instituicao, de, ate, prazo });
    // A busca NÃO entra aqui: ela dispara no submit, não a cada tecla. Uma fila
    // de 13 mil itens consultada a cada letra é uma consulta por caractere, e o
    // resultado que chega é o de uma busca que o operador já abandonou.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fluxo]);

  if (fase === "carregando") {
    return <p className="text-cinza">Carregando a fila…</p>;
  }
  if (fase === "erro") {
    return <Recado tipo="erro" texto="Não conseguimos carregar a fila. Recarregue a página." />;
  }

  const total = fila[0]?.total_na_fila ?? 0;

  return (
    <div className="flex flex-col gap-5">
      {/* A fila de AVISOS vem antes do balcão, e desaparece quando está vazia.
          Ela é o que torna visível o que falta: sem provedor de e-mail
          configurado, os avisos acumulam, e um número na tela é melhor que uma
          fila que ninguém conta. */}
      <FilaDeAvisos />

      <div>
        <h2 className="font-display text-lg font-bold text-titulo">Balcão da secretaria</h2>
        <p className="mt-0.5 text-sm text-cinza" data-contagem>
          {/* O total da FILA (o que espera alguém), não o total da tabela — são
              157.265 documentos ao todo, e 12.625 esperando. Mostrar o número
              grande faria o operador achar que nunca acaba. */}
          {/* O rótulo muda quando o filtro sai da fila de trabalho.
              "17.117 esperando atendimento" é falso: reprovado espera o ALUNO
              reenviar, aprovado não espera ninguém. Rótulo que depende do
              contexto para ser entendido quebra no dia em que a peça passa a
              ter dois contextos — a cicatriz do selo "encerrada" do Mapa de
              Concursos. */}
          {/* "Buscando…" mora AQUI desde que o botão de buscar virou lupa: um
              ícone não fala, e o estado em voo tem de estar escrito em algum
              lugar que o operador lê. */}
          {(() => {
            if (buscando) return "Buscando…";
            const emConsulta =
              status !== null && filtros?.status.some((s) => s.valor === status && !s.esperando);
            const oQue = emConsulta ? "nesta consulta" : "esperando atendimento";
            return fila.length === total
              ? `${total.toLocaleString("pt-BR")} ${oQue}`
              : `mostrando ${fila.length} de ${total.toLocaleString("pt-BR")} ${oQue}`;
          })()}
          {!buscando && (algumFiltro || busca) ? " · filtrado" : ""}
        </p>
        {/* Dita UMA vez, aqui. Ela estava em toda linha da fila e a captura
            mostrou "Só é possível entregar anexando o arquivo" repetido 25
            vezes — a mesma informação 25 vezes ocupa altura e não informa mais.
            Medido: 6.198 pedidos da origem foram marcados entregues sem nada, e
            o aluno lia "Documento Entregue" sem ter o que baixar. */}
        <p className="mt-0.5 text-xs text-cinza">
          Entregar exige anexar o arquivo · reprovar exige o motivo
        </p>
      </div>

      {/* UM lugar para tudo que estreita a fila (pedido de Victor em 09/09, com
          o desenho da barra). Antes eram TRÊS faixas — a busca à direita do
          título com um botão "Buscar" que quebrava em duas linhas a 1280px, os
          chips de fluxo, e um cartão com os combos — e o operador procurava o
          filtro em três alturas diferentes da tela.

          A forma é a do desenho: a busca com a lupa DENTRO do campo abre a
          primeira linha com status, instituição e documento; a segunda tem
          curso, turma, o período em duas datas, o prazo e o atalho "Atrasados".
          Os combos não têm rótulo visível — "Todos os status (11.927)" nomeia o
          combo e ainda diz o tamanho da fila —, e o nome acessível vai no
          `aria-label`.

          Continua valendo: só aparece o filtro que tem mais de uma opção (um
          combo de um item é ruído), e opção que levaria a zero na fila inteira
          não aparece. As CONTAGENS saíram das opções por decisão de Victor no
          mesmo dia: elas eram da fila do fluxo, não do recorte de agora, e
          "Todos os prazos (11.921)" depois de escolher um curso é número certo
          para a pergunta errada. A exceção é a
          instituição, que Victor pediu por nome: enquanto NENHUMA instituição
          conhecida está na fila, ela aparece desligada e a nota abaixo diz por
          quê, em vez de sumir — o que a tela não diz, o olho preenche com a
          suposição errada. Foi o estado real na tarde de 09/09
          (`matriculas.instituicao` nula nas 43.386 matrículas); a carga entrou à
          noite (000203, da outra frente) e o combo ligou sozinho, sem deploy. */}
      <BarraDeFiltros>
        {/* O fluxo: o corte grosso, e o que a equipe mais troca de link. */}
        <div className="flex flex-wrap gap-2" role="group" aria-label="Fluxo">
          {(
            [
              { id: null, rotulo: "Tudo" },
              { id: "envio", rotulo: "Documentos enviados" },
              { id: "solicitacao", rotulo: "Pedidos" },
            ] as { id: "envio" | "solicitacao" | null; rotulo: string }[]
          ).map((f) => (
            <button
              key={f.rotulo}
              type="button"
              onClick={() => trocarFluxo(f.id)}
              aria-pressed={fluxo === f.id}
              data-fluxo={f.id ?? "tudo"}
              // `text-alerta-tinta` e não `laranja-texto`: é texto pequeno sobre
              // fundo claro, e #c76a00 dá 3,83 : 1 (a medição do runbook dos
              // cursos bônus). `alerta-tinta` troca com o tema.
              className={`rounded-full border px-4 py-1.5 text-sm font-semibold transition ${
                fluxo === f.id
                  ? "border-laranja bg-laranja/10 text-alerta-tinta"
                  : "border-borda text-cinza hover:border-laranja/50"
              }`}
            >
              {f.rotulo}
            </button>
          ))}
        </div>

        {/* Linha 1: a busca abre, e os três combos que mais se usam. */}
        <LinhaDeFiltros n={1}>
          <CampoDeBusca
            valor={busca}
            aoMudar={setBusca}
            placeholder="Buscar por nome, e-mail ou documento"
            rotulo="Buscar na fila"
            aoBuscar={() => carregar(fluxo, busca)}
            buscando={buscando}
            aoLimpar={() => {
              setBusca("");
              carregar(fluxo, "");
            }}
            data-busca
            className="min-w-[18rem] flex-1"
          />
          {filtros && filtros.status.length > 1 && (
            <Filtro
              rotulo="Status"
              rotuloTodos="Todos os status"
              chave="status"
              valor={status}
              aoTrocar={(v) => trocar({ status: v })}
              opcoes={filtros.status.map((s) => ({
                valor: s.valor,
                // Só o nome do status, a pedido de Victor em 22/09.
                //
                // Ele já teve "· consulta" nos dois que não esperam ninguém
                // (`aprovado` e `reprovado`), para avisar que escolhê-los troca a
                // fila de trabalho por uma consulta de dezenas de milhares de
                // itens. O aviso era verdadeiro e estava no lugar errado: um
                // rótulo de combo é o NOME da coisa, e quem lê "Aprovados ·
                // consulta" precisa decifrar o sufixo antes de entender a opção.
                // O tamanho do recorte a tela já diz no cabeçalho, depois da
                // escolha, com o número real.
                rotulo: ROTULO_STATUS[s.valor] ?? s.valor,
                itens: s.itens,
              }))}
            />
          )}
          {filtros && (
            <Filtro
              rotulo="Instituição"
              // O que a origem chama de FBC e Focus, de onde sai o diploma
              // (000137). "Todas" e não "FBC + Focus": enquanto o dado não vem,
              // nomear as duas no padrão prometeria uma separação que não existe.
              rotuloTodos="Todas as instituições"
              chave="instituicao"
              valor={instituicao}
              aoTrocar={(v) => trocar({ instituicao: (v as Instituicao | null) ?? null })}
              // Desligado enquanto NENHUMA instituição conhecida está na fila
              // (o estado da tarde de 09/09). Combo ligado com uma opção
              // mentiria que dá para separar; combo sumido faria Victor
              // procurar o que pediu.
              desligado={!temInstituicao}
              opcoes={INSTITUICOES.map((i) => ({
                valor: i,
                rotulo: ROTULO_INSTITUICAO[i],
                itens: filtros.instituicoes.find((x) => x.valor === i)?.itens ?? 0,
              }))}
            />
          )}
          {/* O tipo de documento: 24 no catálogo, e "atender os históricos" é
              como a equipe organiza o trabalho. Pelo NOME e não pelo id, porque
              11 nomes históricos ("RG (Frente)", "CPF (Verso)"…) só existem em
              `nome_origem` — filtrar por id perderia os antigos (000161). */}
          {filtros && filtros.tipos.length > 1 && (
            <Filtro
              rotulo="Documento"
              rotuloTodos="Todos os documentos"
              chave="documento"
              valor={tipo}
              aoTrocar={(v) => trocar({ tipo: v })}
              opcoes={filtros.tipos.map((t) => ({ valor: t.nome, rotulo: t.nome, itens: t.itens }))}
            />
          )}
        </LinhaDeFiltros>

        {/* Linha 2: onde e quando — curso, turma, o período e o prazo. */}
        {filtros && (
          <LinhaDeFiltros n={2}>
            {filtros.cursos.length > 1 && (
              <Filtro
                rotulo="Curso"
                rotuloTodos="Todos os cursos"
              chave="curso"
                valor={curso}
                aoTrocar={(v) => trocar({ curso: v })}
                opcoes={filtros.cursos.map((c) => ({
                  valor: c.slug,
                  // O nome longo da pós tem 74 caracteres e estoura o combo; o slug
                  // é o que a equipe usa para falar do curso.
                  rotulo: c.slug,
                  // SEM `itens`, e é o único combo assim (000280). Os outros
                  // cortam a opção com zero, porque oferecer um tipo de documento
                  // que ninguém enviou é caminho morto. Aqui a lista é dos cursos
                  // que a casa VENDE, e "nenhum documento" é uma resposta — foi
                  // assim que a Pós Auditoria (124 matrículas, zero documentos)
                  // sumiu do filtro e pareceu defeito a quem procurou por ela.
                }))}
              />
            )}
            {/* A turma só faz sentido DENTRO de um curso: a turma 9 existe em
                vários, e listar todas juntas somaria alunos de cursos diferentes
                no mesmo número. */}
            {curso && filtros.turmas.filter((t) => t.curso_slug === curso).length > 1 && (
              <Filtro
                rotulo="Turma"
                rotuloTodos="Todas as turmas"
              chave="turma"
                valor={turma}
                aoTrocar={(v) => trocar({ turma: v })}
                opcoes={filtros.turmas
                  .filter((t) => t.curso_slug === curso)
                  .map((t) => ({ valor: String(t.numero), rotulo: `Turma ${t.numero}`, itens: t.itens }))}
              />
            )}
            {/* O período é sobre a data do pedido/envio, inclusivo nas duas
                pontas (000201). `min`/`max` são as pontas da fila: um calendário
                aberto em 1970 convida a filtrar o nada. */}
            <CampoDeData
              rotulo="Pedido ou envio a partir de"
              chave="de"
              valor={de}
              min={filtros.periodo.de}
              max={ate ?? filtros.periodo.ate}
              aoTrocar={(v) => trocar({ de: v })}
            />
            <CampoDeData
              rotulo="Pedido ou envio até"
              chave="ate"
              valor={ate}
              min={de ?? filtros.periodo.de}
              max={filtros.periodo.ate}
              aoTrocar={(v) => trocar({ ate: v })}
            />
            {/* O prazo, com a MESMA definição da etiqueta ATRASADO da linha:
                dias esperando > prazo do tipo. Medido em 09/09: 94,6% da fila já
                está atrasada, então o COMPLEMENTO ("No prazo") é o corte útil. */}
            <Filtro
              rotulo="Prazo"
              rotuloTodos="Todos os prazos"
              chave="prazo"
              valor={prazo}
              aoTrocar={(v) => trocar({ prazo: (v as Prazo | null) ?? null })}
              opcoes={PRAZOS.map((p) => ({ valor: p, rotulo: ROTULO_PRAZO[p], itens: filtros.prazos[p] }))}
            />
            {/* O atalho do desenho: o MESMO filtro de prazo, num clique. Ligado ao
                mesmo parâmetro da query — não é um segundo estado, é uma segunda
                alça para o mesmo. */}
            <button
              type="button"
              aria-pressed={prazo === "atrasado"}
              onClick={() => trocar({ prazo: prazo === "atrasado" ? null : "atrasado" })}
              data-atrasados
              className={`h-12 rounded-xl border-[1.5px] px-4 text-sm font-semibold transition ${
                prazo === "atrasado"
                  ? "border-laranja bg-laranja/10 text-alerta-tinta"
                  : "border-borda text-tinta hover:border-laranja/50"
              }`}
            >
              {/* Sem o número, pela mesma razão dos combos: ele era da fila
                  inteira e não mudava com os outros filtros. */}
              Atrasados
            </button>
          </LinhaDeFiltros>
        )}

        {filtros && !temInstituicao && (
          <p className="text-xs text-cinza" data-nota-instituicao>
            Instituição: o dado ainda não foi carregado — {" "}
            {(filtros.instituicoes.find((x) => x.valor === "sem")?.itens ?? 0).toLocaleString("pt-BR")}{" "}
            documentos na fila sem instituição conhecida. O filtro liga sozinho quando a carga
            chegar.
          </p>
        )}

        {(algumFiltro || busca) && (
          <LimparFiltros
            aoLimpar={() => {
              setBusca("");
              trocar(
                {
                  status: null,
                  curso: null,
                  turma: null,
                  tipo: null,
                  instituicao: null,
                  de: null,
                  ate: null,
                  prazo: null,
                },
                "",
              );
            }}
          />
        )}
      </BarraDeFiltros>

      {recado && <Recado tipo={recado.tipo} texto={recado.texto} />}

      {fila.length === 0 ? (
        <div className="surface p-6 text-cinza">
          {busca
            ? `Nada na fila para "${busca}".`
            : "A fila está vazia — nada esperando atendimento."}
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-3">
            {fila.slice(0, mostrando).map((item) => (
              <LinhaDaFila
                key={item.id}
                item={item}
                aoTerminar={async (r) => {
                  setRecado(r);
                  await carregar(fluxo, busca);
                }}
              />
            ))}
          </div>

          {mostrando < fila.length && (
            <div className="flex flex-col items-center gap-2">
              <button
                type="button"
                onClick={() => setMostrando((n) => n + POR_PAGINA)}
                data-mostrar-mais
                className="btn-contorno text-sm"
              >
                Mostrar mais {Math.min(POR_PAGINA, fila.length - mostrando)}
              </button>
              {/* O que já apareceu, e de quanto. Sem isto o operador não sabe se
                  está no começo ou no fim de uma fila de 12 mil. */}
              <p className="text-xs text-cinza">
                {mostrando} de {fila.length} carregados
                {fila.length < total ? ` · ${total} na fila` : ""}
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function LinhaDaFila({
  item,
  aoTerminar,
}: {
  item: ItemDaFila;
  aoTerminar: (r: { tipo: "ok" | "erro"; texto: string }) => void;
}) {
  const [motivo, setMotivo] = useState("");
  // Aberto só ao clicar em "Reprovar…" — ver o comentário no JSX. Aprovar é 85%
  // dos casos, e o campo que serve à minoria fazia a fila ter 10 telas.
  const [reprovando, setReprovando] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const arquivo = useRef<HTMLInputElement>(null);
  const estado = estadoDoDocumento(item);

  /**
   * O prazo estourou?
   *
   * Só aparece quando o tipo TEM prazo (os quatro de solicitação têm, os de
   * envio não) e quando ele passou. Um selo "no prazo" em toda linha seria
   * ruído; o que o operador procura é o que está atrasado.
   */
  const atrasado = item.prazo_dias !== null && item.dias_esperando > item.prazo_dias;

  async function avaliar(aprovado: boolean) {
    setOcupado(true);
    const r = await avaliarEnvio(item.id, aprovado, motivo.trim() || null);
    setOcupado(false);
    aoTerminar(
      r.ok
        ? { tipo: "ok", texto: `${item.nome} ${aprovado ? "aprovado" : "reprovado"}.` }
        : { tipo: "erro", texto: r.erro ?? "não deu certo" },
    );
  }

  /**
   * Reprocessa a análise da IA deste envio — o botão que Victor pediu em 09/09:
   * *"a secretaria pode estar ali trabalhando e reprocessar um envio de
   * documentos"*.
   *
   * DUAS etapas, e a ordem é obrigatória: `sec_dados_para_analise` recusa quem
   * não está `em_analise` (*"a análise não desfaz decisão"*), então o envio
   * reprovado volta à fila ANTES. A RPC responde ok sem escrever nada quando ele
   * já está lá, e é ela — não esta função — que decide se pode: aprovado não
   * volta, e reprovação de uma PESSOA não é desfeita pela IA (000211).
   *
   * Os três desfechos da análise dão mensagens diferentes porque a ação do
   * operador é diferente em cada um, e a distinção é a razão de a análise
   * existir: `nada` é falha NOSSA, e 86,3% das 7.036 reprovações de "não foi
   * possível ler" da origem eram exatamente isso — o arquivo estava perfeito.
   * Dizer "reprovado" ali seria repetir o defeito que o hub veio consertar.
   */
  async function reprocessar() {
    setOcupado(true);
    const r = await reabrirParaAnalise(item.id);
    if (!r.ok) {
      setOcupado(false);
      // O `detalhe` do banco é a frase que instrui ("avalie à mão"), e é por
      // isso que a tela não monta mensagem própria aqui.
      aoTerminar({
        tipo: "erro",
        texto: r.detalhe ?? `não deu para reabrir para análise (${r.motivo})`,
      });
      return;
    }

    const a = await analisarDocumento(item.id);
    setOcupado(false);

    if (!a.analisado) {
      aoTerminar({
        tipo: "erro",
        texto:
          `A IA não rodou (${a.motivo})${a.detalhe ? `: ${a.detalhe}` : ""}. ` +
          `O envio está na fila para atendimento.`,
      });
      return;
    }
    if (a.aplicou === "aprovado") {
      aoTerminar({
        tipo: "ok",
        texto: `${item.nome} de ${item.aluno_nome ?? "o aluno"}: a IA APROVOU no reprocessamento.`,
      });
      return;
    }
    if (a.aplicou === "reprovado") {
      aoTerminar({
        tipo: "ok",
        texto: `${item.nome}: a IA reprovou de novo — ${a.mensagem ?? "sem mensagem"}`,
      });
      return;
    }
    aoTerminar({
      tipo: "erro",
      texto:
        `A IA não conseguiu julgar ${a.arquivos === 1 ? "o arquivo" : `os ${a.arquivos} arquivos`}` +
        ` — a falha foi nossa, não do documento. O envio continua na fila; veja a análise para o motivo técnico.`,
    });
  }

  /**
   * Dispara a emissão automática de um pedido que já estava na fila.
   *
   * A mensagem distingue os três desfechos, porque a ação do operador é
   * diferente em cada um: emitido e entregue (nada a fazer), emitido e em
   * conferência (ele mesmo vai aprovar), e recusado (o `detalhe` diz o que
   * falta — aqui, ao contrário da tela do aluno, o texto técnico SERVE: quem lê
   * é quem pode agir sobre ele).
   */
  async function emitir() {
    setOcupado(true);
    const r = await emitirDocumento(item.id);
    setOcupado(false);
    if (r.emitido) {
      aoTerminar({
        tipo: "ok",
        texto: r.emConferencia
          ? `Certificado ${r.numero} emitido para ${item.aluno_nome ?? "o aluno"}. Confira e libere abaixo.`
          : `${item.nome} emitido (${r.numero}) e entregue a ${item.aluno_nome ?? "o aluno"}.`,
      });
      return;
    }
    aoTerminar({
      tipo: "erro",
      texto: r.detalhe
        ? `Não emitiu: ${r.detalhe}`
        : `Não emitiu (${r.motivo}). O pedido continua na fila.`,
    });
  }

  async function conferir(aprovado: boolean) {
    setOcupado(true);
    const r = await conferirCertificado(item.id, aprovado, motivo.trim() || null);
    setOcupado(false);
    aoTerminar(
      r.ok
        ? {
            tipo: "ok",
            texto: aprovado
              ? `Certificado liberado para ${item.aluno_nome ?? "o aluno"}. Ele recebe um e-mail.`
              : `Certificado recusado. O pedido voltou para a fila com o motivo.`,
          }
        : { tipo: "erro", texto: r.erro ?? "não deu certo" },
    );
  }

  async function entregar(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setOcupado(true);
    const r = await entregarSolicitacao(item.id, f);
    setOcupado(false);
    aoTerminar(
      r.ok
        ? { tipo: "ok", texto: `${item.nome} entregue para ${item.aluno_nome ?? "o aluno"}.` }
        : { tipo: "erro", texto: r.erro ?? "não deu certo" },
    );
  }

  return (
    <div
      data-fila-item={item.id}
      className={`surface flex flex-col gap-4 p-5 ${atrasado ? "border-l-4 border-l-laranja" : ""}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-56 flex-1">
          <p className="font-display font-semibold text-titulo">{item.nome}</p>
          <p className="mt-0.5 text-sm text-cinza">
            {item.aluno_nome ?? "sem nome"} · {item.aluno_email ?? "sem e-mail"}
            {item.curso_slug ? ` · ${item.curso_slug}` : ""}
            {/* A turma (000159). Só aparece quando existe: o aluno de curso livre
                e a matrícula sem turma definida não têm, e um "turma —" em toda
                linha seria ruído. */}
            {item.turma_numero !== null ? ` · turma ${item.turma_numero}` : ""}
          </p>
          {/* A INSTITUIÇÃO, e só quando ela NÃO é a FBC (000252).
           *
           * O hub emite documento da FBC e nada mais (000248), então "FBC" em
           * toda linha diria o que a ferramenta inteira já diz — é o "zero
           * grande é ruído" de novo. O que muda a conduta do operador é a
           * exceção, e ela ganha o chip de ATENÇÃO (`alerta`, laranja; `aviso`
           * nesta casa é VERDE e significa confirmação).
           *
           * Silêncio aqui significa FBC sem ambiguidade, porque o nulo tem chip
           * próprio: a instituição vai IMPRESSA no documento, e "não sei" não é
           * "fbc" — é o que a emissão recusa com mensagem separada. */}
          {item.instituicao !== "fbc" && (
            <p className="mt-1.5">
              <span
                data-instituicao={item.instituicao ?? "sem-registro"}
                className="rounded-full border border-alerta-tinta/40 bg-alerta-fundo px-2.5 py-0.5 text-xs font-semibold text-alerta-tinta"
              >
                {item.instituicao
                  ? `matrícula da ${item.instituicao.toUpperCase()}`
                  : "instituição não registrada"}
              </span>
            </p>
          )}
          <p className="mt-1 text-xs text-cinza">
            {item.dias_esperando === 0
              ? "chegou hoje"
              : `esperando há ${item.dias_esperando} dia${item.dias_esperando === 1 ? "" : "s"}`}
            {item.prazo_dias ? ` · prazo de ${item.prazo_dias} dias` : ""}
            {atrasado ? " · ATRASADO" : ""}
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-3">
          <VerAnexos item={item} />
          <span className="rounded-full bg-chip px-3 py-1 text-xs font-semibold text-cinza">
            {estado.rotulo}
          </span>
        </div>
      </div>

      {/* A mensagem que já existe — o operador precisa ver o que foi dito antes
          de dizer de novo. `whitespace-pre-line` pelo mesmo motivo da tela do
          aluno: são 4.492 mensagens multi-linha, uma por motivo. */}
      {item.mensagem && (
        <p className="whitespace-pre-line rounded-cf bg-chip p-3 text-sm text-tinta">
          <span className="font-semibold">Último retorno: </span>
          {item.mensagem}
        </p>
      )}

      {/* A análise da IA fica FORA da linha de ações do cabeçalho, e não junto do
          "Ver arquivo": aquele bloco é `shrink-0` numa linha, e o painel aberto é
          um bloco de largura cheia — dentro dali ele espremeria o resto. Aqui ele
          cresce para baixo, ao lado do que o aluno leu. */}
      <VerAnalise item={item} />

      {item.fluxo === "envio" ? (
        /* O campo de motivo abre SÓ ao clicar em "Reprovar" (000159).
         *
         * MEDIDO: com ele sempre aberto, 25 itens da fila davam 5.215px no
         * desktop e 9.005px no celular — dez telas de rolagem —, e a captura
         * mostrava "obrigatório para reprovar" repetido 25 vezes. Aprovar é o
         * caso comum (100.969 aprovações contra 17.117 reprovações, ou seja 85%),
         * então o campo que serve à minoria ocupava altura em toda linha.
         *
         * É a mesma classe do IR Pro (8.249px para mostrar 34 títulos) e da fila
         * que já foi de 200 para 25 itens por página. */
        <div className="flex flex-col gap-3">
          {reprovando ? (
            <>
              <label className="text-sm font-semibold text-titulo">
                Motivo da reprovação
                <span className="ml-2 font-normal text-cinza">
                  é o que o aluno lê para corrigir
                </span>
                <textarea
                  value={motivo}
                  onChange={(e) => setMotivo(e.target.value)}
                  rows={2}
                  autoFocus
                  data-motivo
                  className={`${CAMPO} mt-1 w-full`}
                  placeholder="Ex.: a foto está cortada; reenvie o documento inteiro."
                />
              </label>
              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => avaliar(false)}
                  // A tela cobra o motivo ANTES do banco. A constraint também
                  // recusa, mas "violação de check" não diz ao operador o que fazer.
                  disabled={ocupado || !motivo.trim()}
                  data-reprovar
                  className="pill-cta disabled:opacity-60"
                  title={motivo.trim() ? undefined : "Escreva o motivo para poder reprovar"}
                >
                  {ocupado ? "Reprovando…" : "Confirmar reprovação"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setReprovando(false);
                    setMotivo("");
                  }}
                  className="text-sm font-semibold text-cinza underline-offset-4 hover:underline"
                >
                  cancelar
                </button>
              </div>
            </>
          ) : (
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => avaliar(true)}
                disabled={ocupado}
                data-aprovar
                className="pill-cta disabled:opacity-60"
              >
                {ocupado ? "Aprovando…" : "Aprovar"} <SetaDireita />
              </button>
              <button
                type="button"
                onClick={() => setReprovando(true)}
                disabled={ocupado}
                data-abrir-reprovar
                className="btn-contorno text-sm disabled:opacity-40"
              >
                Reprovar…
              </button>

              {/* REPROCESSAR a análise da IA (000211).
               *
               * Só nos dois estados em que ele age: `em_analise` (a fila padrão
               * do balcão — a análise falhou por causa nossa, ou o envio nunca
               * passou por ela) e `reprovado` (a consulta que o filtro de status
               * alcança, 13.601 itens, dos quais 3.325 nunca foram lidos pela
               * nossa IA). No APROVADO o botão não aparece, porque a ação não se
               * aplica: reanalisar poderia tirar do aluno um documento que já
               * está certo, e a RPC recusa.
               *
               * Isto não é a cicatriz do "Limite com a prorrogação", em que
               * esconder uma linha escondia um AVISO — aqui o que não aparece é
               * uma AÇÃO que não existe naquele estado. Aviso ausente engana;
               * ação ausente é o certo.
               *
               * O rótulo diz que demora: MEDIDO no reprocessamento em lote, são
               * ~20 s por envio, gastos em baixar o arquivo e esperar o modelo.
               * Sem esse aviso o operador clica de novo achando que não pegou. */}
              {(item.status === "em_analise" || item.status === "reprovado") && (
                <button
                  type="button"
                  onClick={reprocessar}
                  disabled={ocupado}
                  data-reprocessar
                  title="A IA lê os arquivos de novo e decide. Leva alguns segundos."
                  className="btn-contorno text-sm disabled:opacity-40"
                >
                  {ocupado ? "Analisando…" : "Reprocessar com IA"}
                </button>
              )}
            </div>
          )}
        </div>
      ) : item.status === "aguardando_conferencia" ? (
        /* A validação manual do certificado (000149/000152/000153).
         *
         * Pedido de Victor em 01/09: *"o n8n emite mas ele não fica disponível de
         * imediato pro aluno, ele passa pela validação manual da secretaria que
         * aprova/reprova o certificado emitido"*.
         *
         * O PDF sai de `emitido_path`, e não de `anexo_path`: o do PEDIDO é nulo
         * neste estado de propósito, porque é ele que a policy do bucket usa para
         * liberar o download ao aluno. Sem isso a tela pediria para conferir um
         * documento que não abre. */
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-cf bg-chip p-3">
            <p className="text-sm text-tinta">
              <span className="font-semibold">Certificado emitido</span>
              {item.emitido_numero ? ` · ${item.emitido_numero}` : ""}
              <span className="mt-0.5 block text-xs text-cinza">
                O aluno ainda NÃO recebeu. Confira antes de liberar.
              </span>
            </p>
            {item.emitido_path ? (
              <VerArquivo caminho={item.emitido_path} rotulo="Ver o certificado" />
            ) : (
              /* Sem arquivo, conferir é impossível — e a ausência de uma linha
                 não informa nada (a cicatriz do "Limite com a prorrogação" do HUB
                 DP). Acontece se a emissão foi reservada e o emissor falhou: o
                 caminho é reemitir, e a tela diz isso. */
              <p className="text-xs font-semibold text-erro-tinta">
                A emissão não trouxe arquivo — reemita antes de conferir.
              </p>
            )}
          </div>

          <label className="text-sm font-semibold text-titulo">
            Motivo da recusa
            <span className="ml-2 font-normal text-cinza">
              obrigatório para recusar — é o que a equipe lê para corrigir e reemitir
            </span>
            <textarea
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              rows={2}
              data-motivo-conferencia
              className={`${CAMPO} mt-1 w-full`}
              placeholder="Ex.: o nome do curso saiu abreviado."
            />
          </label>

          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              disabled={ocupado || !item.emitido_path}
              data-aprovar-certificado
              onClick={() => conferir(true)}
              className="pill-cta disabled:opacity-60"
              title={item.emitido_path ? undefined : "Sem arquivo emitido não há o que aprovar"}
            >
              {ocupado ? "Liberando…" : "Aprovar e liberar"} <SetaDireita />
            </button>
            <button
              type="button"
              // A tela cobra o motivo ANTES do banco: a RPC também recusa, mas
              // "recusa_sem_motivo" não diz ao operador o que fazer.
              disabled={ocupado || !motivo.trim()}
              data-recusar-certificado
              onClick={() => conferir(false)}
              className="btn-contorno text-sm disabled:opacity-40"
              title={motivo.trim() ? undefined : "Escreva o motivo para poder recusar"}
            >
              Recusar
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-3">
          {/* "Emitir agora" — o gatilho que faltava (000157).
           *
           * Medido em 02/09: 7.344 pedidos com modelo automático estavam
           * esperando na fila sem ninguém poder dispará-los, porque
           * `emitirDocumento` tinha UM chamador — o clique do aluno. A equipe
           * abria a fila e a única ação era "Anexar e entregar", que é
           * exatamente o trabalho manual que a emissão existe para eliminar.
           *
           * Só aparece onde há modelo (`tipo_emissao`) e onde o pedido ESPERA
           * emissão: em `aguardando_pagamento` a RPC recusa por estado (000156),
           * e em `aguardando_conferencia` o caminho é conferir.
           *
           * E desde a 000252 ele também não aparece onde a instituição não é a
           * FBC: a guarda da 000248 recusaria, e o botão que sempre recusa é
           * pior que botão ausente. No lugar dele vai o MOTIVO, abaixo — não um
           * `pill-desligada`, porque aqui não falta permissão: falta uma ação, e
           * ela é fora do hub.
           *
           * O TEXTO repete o `detalhe` que `sec_dados_para_emissao` devolveria,
           * e a repetição é consciente: perguntar ao banco linha por linha
           * custaria uma chamada por item da página. O que NÃO se repete é a
           * DECISÃO — quem recusa continua sendo a guarda, e a prova 6 da 000252
           * exige que a coluna e a guarda concordem. Se divergirem, o operador
           * vê a frase e o clique funciona (ou o contrário), e é a prova que
           * grita antes de alguém notar. */}
          {item.tipo_emissao !== "nenhuma" &&
            item.status === "aguardando_documento" &&
            (item.instituicao === "fbc" ? (
              <button
                type="button"
                disabled={ocupado}
                data-emitir
                onClick={emitir}
                className="pill-cta disabled:opacity-60"
              >
                {ocupado ? "Emitindo…" : "Emitir agora"} <SetaDireita />
              </button>
            ) : (
              <p
                data-sem-emissao={item.instituicao ?? "sem-registro"}
                className="rounded-cf border border-alerta-tinta/40 bg-alerta-fundo p-3 text-sm text-alerta-tinta"
              >
                {item.instituicao
                  ? `O hub emite documento da FBC, e esta matrícula é da ${item.instituicao.toUpperCase()}. Peça este documento à ${item.instituicao.toUpperCase()}.`
                  : "A instituição desta matrícula não está registrada, e ela vai impressa no documento. Registre-a antes de emitir."}
              </p>
            ))}
          {/* "Confirmar pagamento" é a porta MANUAL, e ela fica.
           *
           * Com o comprovante (000242) o caminho normal é o aluno anexar e a
           * equipe validar — mas os 679 pedidos que já estavam em
           * `aguardando_pagamento` nasceram sob a regra antiga, e 460 deles são
           * de um tipo que HOJE não tem taxa nenhuma. Sem este botão eles não
           * têm saída, porque o aluno não tem link para pagar. */}
          {item.status === "aguardando_pagamento" && (
            <button
              type="button"
              disabled={ocupado}
              data-confirmar-pagamento
              onClick={async () => {
                setOcupado(true);
                const r = await confirmarPagamento(item.id);
                setOcupado(false);
                aoTerminar(
                  r.ok
                    ? { tipo: "ok", texto: "Pagamento confirmado. O pedido foi para a fila." }
                    : { tipo: "erro", texto: r.erro ?? "não deu certo" },
                );
              }}
              className="btn-contorno text-sm disabled:opacity-60"
            >
              Confirmar pagamento
            </button>
          )}
          <label className={`pill-cta cursor-pointer ${ocupado ? "opacity-60" : ""}`}>
            {ocupado ? "Enviando…" : "Anexar e entregar"}
            <input
              ref={arquivo}
              type="file"
              className="sr-only"
              accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/*"
              disabled={ocupado}
              onChange={entregar}
            />
          </label>
        </div>
      )}

      {/* ── O COMPROVANTE de pagamento, quando há um esperando (000242) ─────
          Fora do `flex` das ações porque é um painel, não um botão: o operador
          precisa ABRIR o arquivo antes de decidir, e uma recusa pede um motivo
          escrito que o aluno vai ler. */}
      {item.status === "comprovante_em_analise" && (
        <PainelComprovante item={item} aoTerminar={aoTerminar} />
      )}
    </div>
  );
}

/**
 * O comprovante que o aluno anexou, e a decisão da secretaria (000242).
 *
 * Carrega sob demanda — a fila mostra 25 itens e o operador vai abrir poucos, e
 * buscar o comprovante de todos na carga seriam 25 idas ao banco. É a mesma
 * escolha do painel de anexos de envio, logo abaixo.
 *
 * A RECUSA exige motivo, e o campo é obrigatório na TELA porque a RPC recusa sem
 * ele: deixar o operador clicar e receber "sem_motivo" seria fazê-lo descobrir a
 * regra pelo erro. E o motivo vai para o aluno com todas as letras — é o que
 * impede o reenvio do mesmo arquivo.
 */
function PainelComprovante({
  item,
  aoTerminar,
}: {
  item: ItemDaFila;
  aoTerminar: (r: { tipo: "ok" | "erro"; texto: string }) => void;
}) {
  const [lista, setLista] = useState<ComprovanteNoBalcao[] | null>(null);
  const [motivo, setMotivo] = useState("");
  const [ocupado, setOcupado] = useState(false);

  useEffect(() => {
    let vivo = true;
    lerComprovantesDoPedido(item.id).then((l) => {
      if (vivo) setLista(l);
    });
    return () => {
      vivo = false;
    };
  }, [item.id]);

  const atual = lista?.find((c) => c.situacao === "em_analise") ?? null;

  return (
    <div
      data-painel="comprovante"
      className="mt-3 rounded-lg border border-borda bg-fundo p-4"
    >
      <p className="text-sm font-semibold text-titulo">Comprovante de pagamento</p>

      {lista === null && <p className="mt-1 text-sm text-cinza">Carregando…</p>}

      {lista !== null && !atual && (
        <p className="mt-1 text-sm text-cinza">
          Não achamos o comprovante em análise deste pedido. Recarregue a fila.
        </p>
      )}

      {atual && (
        <>
          <p className="mt-1 text-sm text-cinza">
            Enviado em {new Date(atual.enviado_em).toLocaleDateString("pt-BR")}
            {lista && lista.length > 1 ? ` · ${lista.length}º envio` : ""}
          </p>

          <div className="mt-2">
            <VerArquivo caminho={atual.anexo_path} rotulo="Abrir comprovante" />
          </div>

          {/* As recusas ANTERIORES ficam à vista: na segunda tentativa a
              pergunta do operador é o que já foi recusado e por quê. */}
          {lista && lista.some((c) => c.situacao === "recusado") && (
            <ul className="mt-3 space-y-1 border-t border-borda pt-2">
              {lista
                .filter((c) => c.situacao === "recusado")
                .map((c) => (
                  <li key={c.seq} className="text-xs text-cinza">
                    {c.decidido_em
                      ? new Date(c.decidido_em).toLocaleDateString("pt-BR")
                      : "—"}{" "}
                    · recusado
                    {c.decidido_por ? ` por ${c.decidido_por}` : ""}
                    {c.motivo ? `: ${c.motivo}` : ""}
                  </li>
                ))}
            </ul>
          )}

          <label className="mt-3 block text-sm text-tinta">
            Motivo da recusa
            <span className="text-cinza"> (obrigatório só para recusar)</span>
            <input
              type="text"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="O que o aluno precisa corrigir"
              data-campo="motivo-recusa"
              className="mt-1 w-full rounded-lg border border-borda bg-superficie p-2 text-sm text-tinta placeholder:text-cinza focus:border-acento focus:outline-none"
            />
          </label>

          <div className="mt-3 flex flex-wrap items-center gap-3">
            <button
              type="button"
              disabled={ocupado}
              data-aprovar-comprovante
              onClick={async () => {
                setOcupado(true);
                const r = await aprovarComprovante(item.id);
                setOcupado(false);
                aoTerminar(
                  r.ok
                    ? { tipo: "ok", texto: r.detalhe || "Pagamento validado." }
                    : { tipo: "erro", texto: r.erro },
                );
              }}
              className="pill-cta text-sm disabled:opacity-60"
            >
              {ocupado ? "Validando…" : "Validar pagamento"}
            </button>

            <button
              type="button"
              disabled={ocupado || motivo.trim() === ""}
              data-recusar-comprovante
              onClick={async () => {
                setOcupado(true);
                const r = await recusarComprovante(item.id, motivo);
                setOcupado(false);
                if (r.ok) setMotivo("");
                aoTerminar(
                  r.ok
                    ? { tipo: "ok", texto: r.detalhe || "Comprovante recusado." }
                    : { tipo: "erro", texto: r.erro },
                );
              }}
              // Desabilitado PERMANENTE (enquanto não há motivo) pede cor
              // própria, não opacidade: é a medição de 01/09 — `opacity-50` num
              // CTA dá 2,73 : 1 sobre branco. Aqui o botão é de contorno, então
              // o que muda é o texto ficar `cinza` em vez de `erro-tinta`.
              className={
                motivo.trim() === ""
                  ? "btn-contorno cursor-not-allowed text-sm text-cinza"
                  : "btn-contorno text-sm text-erro-tinta"
              }
            >
              Recusar comprovante
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Um filtro da fila: `<select>` nativo com a contagem em cada opção.
 *
 * Nativo e não combo próprio: o `<select>` do sistema já traz busca por
 * digitação, teclado e rolagem em lista longa — a Pós RT tem 20 turmas —, e é a
 * mesma escolha do `<dialog>` nativo dos modais da casa.
 *
 * A opção "todos" mostra o TOTAL, então o operador vê o tamanho da fila antes de
 * estreitar.
 */
/**
 * Os arquivos de um ENVIO, que agora podem ser até cinco (000162/000163).
 *
 * Carrega sob demanda, ao clicar: a fila mostra 25 itens, e buscar os anexos de
 * todos na carga seriam 25 idas ao banco para um operador que vai abrir dois.
 *
 * A união dos dois lugares onde o arquivo pode estar (a tabela nova e a coluna
 * antiga dos 118.519 envios) mora no BANCO — `sec_anexos_do_documento`. Repetir
 * aqui seria a segunda definição de "onde está o arquivo".
 */
/**
 * O que a IA decidiu neste envio, e por quê.
 *
 * É a tela que a decisão de Victor exigia e não existia: *"Os reprovados serão
 * analisados pelo time da secretaria no painel."* Sem ela a análise é uma caixa
 * preta — a equipe não consegue auditar a IA, nem medir a taxa dela, nem revisar
 * um caso.
 *
 * ---------------------------------------------------------------------------
 * Carrega SOB DEMANDA, e some quando não há análise
 * ---------------------------------------------------------------------------
 * A fila mostra 25 itens; carregar a análise dos 25 seriam 25 idas ao banco para
 * um painel em que o operador abre um. E os 118.519 envios da carga não têm
 * análise nenhuma no hub (a decisão veio do portal antigo), então o botão
 * apareceria em toda linha prometendo o que não existe — a mesma razão de a
 * `FilaDeAvisos` desaparecer no zero.
 *
 * O botão só existe para envios; solicitação não passa por análise.
 */
function VerAnalise({ item }: { item: ItemDaFila }) {
  const [linhas, setLinhas] = useState<AnaliseDoArquivo[] | null>(null);
  const [carregando, setCarregando] = useState(false);

  if (item.fluxo !== "envio") return null;

  if (linhas === null) {
    return (
      <button
        type="button"
        disabled={carregando}
        data-ver-analise
        onClick={async () => {
          setCarregando(true);
          setLinhas(await lerAnalises(item.id));
          setCarregando(false);
        }}
        className="text-sm font-semibold text-laranja-texto underline-offset-4 hover:underline disabled:opacity-60"
      >
        {carregando ? "Abrindo…" : "Ver análise da IA"}
      </button>
    );
  }

  if (!linhas.length) {
    // A frase diz QUAL das duas coisas é: "não passou pela análise do hub" e "a
    // análise não achou nada" produziriam a mesma tela vazia, e o operador
    // precisa da diferença para saber se vale reprocessar.
    return (
      <span className="text-xs text-cinza">
        Este envio não passou pela análise do hub — a decisão veio do portal antigo.
      </span>
    );
  }

  return (
    <div data-analise className="flex w-full flex-col gap-2 rounded-cf bg-chip p-3">
      <span className="text-xs font-semibold uppercase tracking-wide text-cinza">
        Análise automática
      </span>
      {linhas.map((a) => (
        <div key={`${a.ordem}-${a.criado_em}`} className="text-sm text-tinta">
          <span className="font-semibold">
            {linhas.length > 1 ? `Arquivo ${a.ordem}: ` : ""}
            {rotuloDaSituacao(a)}
          </span>
          {/* QUEM reabriu, e os DOIS ramos escritos (000211). O nulo tem texto
              próprio de propósito: "pelo reprocessamento em lote" e "por
              Fulana" levam o operador a lugares diferentes quando ele precisa
              perguntar por que este envio mudou de estado — e a ausência de
              texto seria lida como "sempre foi assim". */}
          {a.situacao === "reaberto" && (
            <span className="text-cinza" data-reaberta-por>
              {a.reaberto_por_nome
                ? ` — por ${a.reaberto_por_nome}`
                : " — pelo reprocessamento em lote"}
            </span>
          )}
          {a.observacoes && <span className="text-cinza"> — {a.observacoes}</span>}

          {/* O `motivos` é ARRAY, e a mensagem que o aluno lê é o join dele. Aqui
              cada motivo é uma linha: o operador está decidindo se concorda, e
              três razões coladas num parágrafo se leem como uma. */}
          {a.motivos && a.motivos.length > 0 && (
            <ul className="ml-4 list-disc text-cinza">
              {a.motivos.map((m, k) => (
                <li key={k}>{m}</li>
              ))}
            </ul>
          )}

          {/* O erro TÉCNICO, que não é do aluno. Aparece aqui e não na tela dele:
              "HTTP 429 depois de 3 tentativas" é informação para quem pode fazer
              algo com ela. */}
          {a.erro && <span className="block text-xs text-cinza">{a.erro}</span>}

          {/* A DIVERGÊNCIA entre os dois campos de decisão do prompt. Medido: 904
              linhas da origem os têm em desacordo, e hoje o caso é invisível —
              produz um documento reprovado dizendo "Documento aprovado com
              sucesso." e ninguém sabe que aconteceu 55 vezes. */}
          {a.divergente && (
            <span className="mt-1 block rounded-cf border border-aviso-borda bg-aviso-fundo px-2 py-1 text-xs text-aviso-tinta">
              A IA se contradisse: escreveu <strong>{a.status_geral}</strong> no resumo e{" "}
              {a.aprovado ? "aprovou" : "reprovou"} na análise técnica. Vale conferir à mão —
              quem decidiu foi a análise técnica.
            </span>
          )}

          {/* A procedência do número, ao lado do número — a regra do teto do MEI
              aplicada aqui. Sem o modelo e o tipo lido, "reprovado pela IA" não é
              auditável: não se sabe qual versão decidiu nem o que ela recebeu. */}
          <span className="block text-xs text-cinza">
            {[a.modelo, a.mime_farejado && `lido como ${a.mime_farejado}`]
              .filter(Boolean)
              .join(" · ")}
          </span>
        </div>
      ))}
    </div>
  );
}

/** O rótulo de cada situação, num lugar só. A tela não monta frase de estado. */
function rotuloDaSituacao(a: AnaliseDoArquivo): string {
  if (a.situacao === "ok") return a.aprovado ? "aprovado pela IA" : "reprovado pela IA";
  switch (a.situacao) {
    case "protegido":
      return "PDF protegido por senha";
    case "formato_recusado":
      return "formato que a análise não lê";
    case "arquivo_ausente":
      return "o arquivo não foi encontrado";
    case "sem_chave":
      return "a análise não estava configurada";
    case "recusado":
      return "o modelo recusou o arquivo";
    case "limite":
      return "limite do modelo — não julgado";
    // Os dois estados que a lista tinha esquecido, e que caíam no default
    // dizendo "a análise falhou" — o que é falso nos dois. `reaberto` é a
    // reabertura registrada (9.215 linhas no banco) e `ilegivel` é o PDF vazio
    // ou danificado da 000175, que reprova pedindo reenvio. É a cicatriz do
    // `aguardando_conferencia`: estado novo obriga a varrer quem lista os
    // antigos, e o modo de falha desta família é sempre silencioso.
    case "reaberto":
      return "reaberta para nova análise";
    case "ilegivel":
      return "arquivo vazio ou danificado";
    default:
      return "a análise falhou — não julgado";
  }
}

function VerAnexos({ item }: { item: ItemDaFila }) {
  const [anexos, setAnexos] = useState<AnexoDoEnvio[] | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState(false);

  // Sem anexo nenhum: nada a oferecer. A ausência do link é a informação, porque
  // o card já diz o estado do documento.
  if (!item.anexo_path && !item.anexo_externo_url) return null;

  if (anexos === null) {
    return (
      <button
        type="button"
        disabled={carregando}
        data-ver-anexos
        onClick={async () => {
          setCarregando(true);
          setErro(false);
          try {
            const lista = await lerAnexos(item.id);
            setAnexos(lista);
            // UM arquivo é o caso comum (109.903 dos 118.519 envios): abre direto,
            // sem obrigar um segundo clique numa lista de um item.
            if (lista.length === 1) {
              const so = lista[0];
              // O bucket do hub PRIMEIRO, a url de fora como segundo ramo — a
              // ordem inverteu em 03/09/2026, com a migração dos anexos. Antes o
              // `anexo_externo_url` vinha antes porque era o único que existia;
              // depois de 144.663 arquivos migrados, mantê-lo na frente faria a
              // secretaria continuar abrindo o CDN do Bubble com o arquivo aqui
              // do lado — e quebrar tudo de uma vez no dia do desligamento. A
              // coluna antiga é preservada de propósito (é o caminho de volta).
              const url = so.anexo_path
                ? await urlDoAnexo(so.anexo_path)
                : so.anexo_externo_url;
              if (url) window.open(url, "_blank", "noopener,noreferrer");
            }
          } catch {
            setErro(true);
          } finally {
            setCarregando(false);
          }
        }}
        className="text-sm font-semibold text-laranja-texto underline-offset-4 hover:underline disabled:opacity-60"
      >
        {carregando ? "Abrindo…" : "Ver arquivo"}
      </button>
    );
  }

  if (erro) {
    return <span className="text-xs font-semibold text-erro-tinta">Não deu para listar</span>;
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      {anexos.length > 1 && (
        <span className="text-xs font-semibold uppercase tracking-wide text-cinza">
          {anexos.length} páginas
        </span>
      )}
      {anexos.map((a) => (
        <VerArquivo
          key={a.ordem}
          caminho={a.anexo_path}
          url={a.anexo_externo_url}
          // Com um arquivo só o rótulo não numera — "página 1 de 1" é ruído.
          rotulo={anexos.length > 1 ? `pág. ${a.ordem}` : "Ver arquivo"}
        />
      ))}
    </div>
  );
}

/**
 * O link para abrir um arquivo do balcão.
 *
 * Recebe CAMINHO e URL em vez do item inteiro porque a fila tem dois arquivos
 * diferentes por linha: o que o aluno enviou (`anexo_path`/`anexo_externo_url`)
 * e o certificado que o hub emitiu e a secretaria vai conferir (`emitido_path`,
 * 000153). Passar o item obrigaria este componente a saber qual dos dois o
 * chamador quer — e o dia em que aparecer um terceiro, ele decide errado.
 */
function VerArquivo({
  caminho,
  url,
  rotulo = "Ver arquivo",
}: {
  caminho?: string | null;
  url?: string | null;
  rotulo?: string;
}) {
  const [abrindo, setAbrindo] = useState(false);
  // O `caminho` (bucket do hub) vem ANTES da `url` externa. Ver a nota no clique
  // do envio, acima: a ordem inverteu com a migração dos anexos, e mantê-la como
  // estava faria a equipe abrir o CDN do Bubble com o arquivo já no hub.
  if (!caminho && url) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className="text-sm font-semibold text-laranja-texto underline-offset-4 hover:underline"
      >
        {rotulo}
        <span className="sr-only"> (abre em outra aba)</span>
      </a>
    );
  }
  if (!caminho) return null;
  return (
    <button
      type="button"
      disabled={abrindo}
      onClick={async () => {
        setAbrindo(true);
        const assinada = await urlDoAnexo(caminho);
        setAbrindo(false);
        if (assinada) window.open(assinada, "_blank", "noopener,noreferrer");
      }}
      className="text-sm font-semibold text-laranja-texto underline-offset-4 hover:underline disabled:opacity-60"
    >
      {abrindo ? "Abrindo…" : rotulo}
    </button>
  );
}
